import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Textarea, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { TemplateSection, TemplateStack } from './template-shell';
import { localDate } from '../contract-form';
import type { ContractFormValues } from '../contract-form';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

interface PaymentRange {
  start_date: string;
  end_date: string;
  price: number;
}

interface PaymentSchedule {
  payment_method?: string;
  payment_mode?: string;
  description?: string;
  ranges?: PaymentRange[];
}

const METHOD_OPTIONS = [
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'postpaid', label: 'Postpaid' },
];
const MODE_OPTIONS = [
  { value: 'one_time', label: 'Pay one time' },
  { value: 'multiple_times', label: 'Pay multiple times' },
];

function endOfMonth(iso: string): string {
  const d = iso ? new Date(iso) : new Date();
  const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const y = eom.getFullYear();
  const m = String(eom.getMonth() + 1).padStart(2, '0');
  const day = String(eom.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nextDay(iso: string): string {
  const d = iso ? new Date(iso) : new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const emptyRange = (): PaymentRange => ({
  start_date: localDate(),
  end_date: endOfMonth(localDate()),
  price: 0,
});

/**
 * Other.Standard template (`OTHER`/`KHÁC` group). Config options stay empty; the
 * payment schedule lives in
 * `service_domain_metadata.other_standard_payment_schedule` — a payment method,
 * description, mode, and one-or-more price ranges. The register price and the
 * product's service-time span are synced from the range totals/bounds.
 */
export function OtherStandardTemplate({ index }: { index: number }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const metaPath = `products.${index}.service_domain_metadata` as const;
  const root = `${metaPath}.other_standard_payment_schedule` as const;
  const schedule = useWatch({ control, name: root as never }) as PaymentSchedule | undefined;

  const paymentMode = schedule?.payment_mode ?? 'one_time';
  const paymentMethod = schedule?.payment_method ?? 'prepaid';
  const ranges = schedule?.ranges?.length ? schedule.ranges : [emptyRange()];

  // Seed the schedule once and clear the config options for OTHER products.
  useEffect(() => {
    if (schedule?.ranges?.length) return;
    setValue(`products.${index}.service_configoptions` as never, [] as never, { shouldDirty: true });
    setValue(
      root as never,
      { payment_method: 'prepaid', payment_mode: 'one_time', description: '', ranges: [emptyRange()] } as never,
      { shouldDirty: true },
    );
  }, [schedule?.ranges?.length, root, index, setValue]);

  // Sync register price + service-time span from the active ranges.
  useEffect(() => {
    const active = paymentMode === 'one_time' ? ranges.slice(0, 1) : ranges;
    const total = active.reduce((sum, r) => sum + Number(r.price || 0), 0);
    const first = active[0];
    const last = active[active.length - 1];
    setValue(`products.${index}.service_price_register`, total, { shouldDirty: true });
    setValue(`products.${index}.prepaid`, paymentMethod === 'prepaid', { shouldDirty: true });
    setValue(`products.${index}.service_price_model`, 'package', { shouldDirty: true });
    if (first) setValue(`products.${index}.first_startdate`, first.start_date, { shouldDirty: true });
    if (last) setValue(`products.${index}.first_enddate`, last.end_date, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, paymentMethod, paymentMode, JSON.stringify(ranges), setValue]);

  // Collapse to one range when switching back to one-time.
  useEffect(() => {
    if (paymentMode !== 'one_time' || ranges.length <= 1) return;
    setValue(`${root}.ranges` as never, ranges.slice(0, 1) as never, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMode]);

  const setField = (field: keyof PaymentSchedule, value: string) =>
    setValue(`${root}.${field}` as never, value as never, { shouldDirty: true });

  const commitRanges = (next: PaymentRange[]) =>
    setValue(`${root}.ranges` as never, next as never, { shouldDirty: true });

  const updateRange = (i: number, key: keyof PaymentRange, value: string | number) =>
    commitRanges(ranges.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const addRange = () => {
    const last = ranges[ranges.length - 1];
    const start = last?.end_date ? nextDay(last.end_date) : localDate();
    commitRanges([...ranges, { start_date: start, end_date: endOfMonth(start), price: 0 }]);
  };

  const removeRange = (i: number) => {
    if (ranges.length <= 1) return;
    commitRanges(ranges.filter((_, idx) => idx !== i));
  };

  const description = schedule?.description ?? '';

  return (
    <TemplateStack>
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Field label="Payment method">
          {(p) => (
            <Select value={paymentMethod} onValueChange={(v) => setField('payment_method', v)}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>
      <Field label="Description">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={description}
            onChange={(e) => setField('description', e.target.value)}
          />
        )}
      </Field>

      <TemplateSection title="Service time">
        <Field label="Payment mode">
          {(p) => (
            <Select value={paymentMode} onValueChange={(v) => setField('payment_mode', v)}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>

        {ranges.map((range, i) => (
          <Card key={i}>
            <CardHeader
              style={{ padding: 'var(--vn-space-2) var(--vn-space-3)' }}
              actions={
                paymentMode === 'multiple_times' && ranges.length > 1 ? (
                  <Button type="button" variant="ghost" size="sm" startIcon={<Trash2 size={14} />} onClick={() => removeRange(i)}>
                    Delete
                  </Button>
                ) : undefined
              }
            >
              <CardTitle>Payment range {i + 1}</CardTitle>
            </CardHeader>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="Start date">
                {(p) => <DateInput {...p} value={range.start_date} onChange={(e) => updateRange(i, 'start_date', e.target.value)} />}
              </Field>
              <Field label="End date">
                {(p) => <DateInput {...p} value={range.end_date} onChange={(e) => updateRange(i, 'end_date', e.target.value)} />}
              </Field>
              <Field label="Register price">
                {(p) => <NumericInput {...p} value={String(range.price ?? 0)} onChange={(e) => updateRange(i, 'price', Number(e.target.value))} />}
              </Field>
            </CardBody>
          </Card>
        ))}

        {paymentMode === 'multiple_times' && (
          <Button type="button" variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addRange}>
            Add payment range
          </Button>
        )}
      </TemplateSection>
    </TemplateStack>
  );
}
